'use client';

import { useProject } from '@/contexts/ProjectContext';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  isRollingWeekSelection,
  last7DaysSelectionFromJobs,
  rangeSelectionFromJobs,
  useRollingRangeAnchor,
  type WeekSelectorValue,
} from '@/components/shared/WeekSelector';
import type { PromptGroup } from '@/types/database-schema';
import { topicOrigin } from '@/utils/topicOptions';
import {
  promptTopicFilterNeedsInventory,
  type AppliedPromptTopicFilter,
  type PromptTopicFilter,
} from '@/utils/promptTopicFilter';
import {
  buildBrandScopeSearchParams,
  normalizeWeekSelectionForGranularity,
  parsePromptIdsParam,
  periodSelectionFromWeekSelection,
  resolveBrandScope,
  resolveWeekSelectionFromParsedScope,
} from '@/utils/brandScope';
import type { PeriodGranularity, WeekJobRecord } from '@/utils/weekJobBuckets';

type ScopeMode = 'with-week' | 'prompt-engine-only';
const EMPTY_SCOPED_PROMPT_IDS: string[] = [];

interface UseBrandRouteScopeOptions {
  promptGroups: PromptGroup[];
  prompts?: readonly { prompt_id: string }[];
  promptsReady?: boolean;
  mode: ScopeMode;
  jobs?: WeekJobRecord[];
  jobsTrustworthy?: boolean;
  defaultPeriodGranularity?: PeriodGranularity;
  defaultToLast7Days?: boolean;
}

interface ScopeHandlers {
  selectedPromptId: string;
  topicScopeReady: boolean;
  handlePromptChange: (promptId: string) => void;
  handlePromptScopeChange: (
    topicId: string,
    promptId: string,
    subtopicId?: string,
    filter?: AppliedPromptTopicFilter
  ) => void;
  handlePromptSubtopicChange: (subtopicId: string) => void;
  replaceScopeInUrl: (next: {
    weekSelection?: WeekSelectorValue;
    periodGranularity?: PeriodGranularity;
    engine?: string;
    promptGroupId?: string;
    promptSubtopicId?: string;
    promptIds?: string[];
    promptId?: string;
    topicFilter?: PromptTopicFilter | null;
  }) => void;
  handleEngineChange: (nextEngine: string) => void;
  handlePromptGroupChange: (nextPromptGroupId: string) => void;
  handleClearFilters: () => void;
  handleClearCustomPromptScope: () => void;
}

interface BrandRouteScopeWithWeek extends ScopeHandlers {
  resolvedScope: ReturnType<typeof resolveBrandScope>;
  scopedPromptIds: string[];
  sqlPromptGroupId?: string;
  promptGroupOrigin?: 'shared';
  promptSubtopicId?: string;
  scopedEngine?: string;
  hasCustomPromptScope: boolean;
  periodGranularity: PeriodGranularity;
  setPeriodGranularity: Dispatch<SetStateAction<PeriodGranularity>>;
  handlePeriodGranularityChange: (next: PeriodGranularity) => void;
  weekSelection: WeekSelectorValue;
  setWeekSelection: Dispatch<SetStateAction<WeekSelectorValue>>;
  handleWeekChange: (next: WeekSelectorValue) => void;
}

interface BrandRouteScopePromptEngineOnly extends ScopeHandlers {
  resolvedScope: ReturnType<typeof resolveBrandScope>;
  scopedPromptIds: string[];
  sqlPromptGroupId?: string;
  promptGroupOrigin?: 'shared';
  promptSubtopicId?: string;
  scopedEngine?: string;
  hasCustomPromptScope: boolean;
  periodGranularity: null;
  setPeriodGranularity: null;
  handlePeriodGranularityChange: null;
  weekSelection: null;
  setWeekSelection: null;
  handleWeekChange: null;
}

export function useBrandRouteScope(
  options: UseBrandRouteScopeOptions & { mode: 'with-week' }
): BrandRouteScopeWithWeek;
export function useBrandRouteScope(
  options: UseBrandRouteScopeOptions & { mode: 'prompt-engine-only' }
): BrandRouteScopePromptEngineOnly;
export function useBrandRouteScope(
  options: UseBrandRouteScopeOptions
): BrandRouteScopeWithWeek | BrandRouteScopePromptEngineOnly {
  const {
    promptGroups,
    prompts,
    promptsReady = false,
    mode,
    jobs = [],
    jobsTrustworthy = false,
    defaultPeriodGranularity = 'week',
    defaultToLast7Days = false,
  } = options;

  const { currentProject, loading: projectLoading, projectsHydrated } = useProject();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const rollingRangeAnchor = useRollingRangeAnchor();
  const selectedPromptId = searchParams.get('prompt_id') || '';
  const topicsHydrated =
    !projectLoading &&
    projectsHydrated &&
    Boolean(currentProject) &&
    (!(currentProject?.prompt_topics_sql || currentProject?.intelligence_collection_linked) ||
      Boolean(currentProject?.topic_options_revision));

  const promptInventoryIds = useMemo(
    () => (promptsReady && prompts ? prompts.map(prompt => prompt.prompt_id) : undefined),
    [prompts, promptsReady]
  );
  const resolvedScope = useMemo(() => {
    const scope = resolveBrandScope(searchParams, promptGroups, promptInventoryIds);
    // An empty topic list during project hydration is not a deleted selection.
    return { ...scope, topicFilterInvalid: topicsHydrated && scope.topicFilterInvalid };
  }, [searchParams, promptGroups, promptInventoryIds, topicsHydrated]);
  const needsPromptInventory = promptTopicFilterNeedsInventory(resolvedScope.topicFilter);
  const explicitPromptIds = searchParams.get('prompt_ids');
  // Keep the original custom IDs: the server intersects them with topic membership,
  // including an empty intersection. Dropping them would broaden to the whole topic.
  const scopedPromptIds = useMemo(
    () =>
      resolvedScope.promptScopeSource === 'filter'
        ? resolvedScope.promptIds
        : explicitPromptIds
          ? parsePromptIdsParam(explicitPromptIds)
          : EMPTY_SCOPED_PROMPT_IDS,
    [explicitPromptIds, resolvedScope.promptScopeSource, resolvedScope.promptIds]
  );
  const selectedTopic = promptGroups.find(
    group =>
      group.id === resolvedScope.promptGroupId &&
      topicOrigin(group) === resolvedScope.promptGroupOrigin
  );
  const selectedSubtopic = selectedTopic?.subtopics?.find(
    child => child.id === resolvedScope.promptSubtopicId
  );
  const childScopeValid =
    !resolvedScope.promptSubtopicId || Boolean(selectedTopic && selectedSubtopic);
  const promptBelongsToScope =
    !selectedPromptId ||
    ((!selectedTopic || selectedTopic.prompt_ids.includes(selectedPromptId)) &&
      (!resolvedScope.promptSubtopicId ||
        Boolean(selectedSubtopic?.prompt_ids.includes(selectedPromptId))) &&
      (!scopedPromptIds.length || scopedPromptIds.includes(selectedPromptId)));
  const topicScopeReady =
    topicsHydrated &&
    (!needsPromptInventory || promptInventoryIds !== undefined) &&
    !resolvedScope.topicFilterInvalid &&
    childScopeValid &&
    (!resolvedScope.promptGroupId || Boolean(selectedTopic)) &&
    promptBelongsToScope;
  const scopedEngine = resolvedScope.engine;
  const hasCustomPromptScope =
    resolvedScope.promptScopeSource === 'legacy_prompt_ids' &&
    !resolvedScope.promptGroupId &&
    resolvedScope.promptIds.length > 0;

  const [weekSelection, setWeekSelection] = useState<WeekSelectorValue>('all');
  const [periodGranularity, setPeriodGranularity] = useState<PeriodGranularity>(() => {
    return resolvedScope.week.periodGrain || defaultPeriodGranularity;
  });
  const hasParsedWeekScope = useMemo(
    () =>
      Boolean(
        resolvedScope.week.rangeKey ||
          resolvedScope.week.jobId ||
          resolvedScope.week.jobIds.length > 0 ||
          resolvedScope.week.weekKey ||
          resolvedScope.week.periodKey
      ),
    [resolvedScope.week]
  );

  useEffect(() => {
    if (mode !== 'with-week') return;
    const nextGranularity = resolvedScope.week.periodGrain || defaultPeriodGranularity;
    setPeriodGranularity(previous => (previous === nextGranularity ? previous : nextGranularity));
  }, [defaultPeriodGranularity, mode, resolvedScope.week.periodGrain]);

  const resolvedRouteWeekSelection = useMemo((): WeekSelectorValue | null => {
    if (mode !== 'with-week' || !jobsTrustworthy) return null;
    if (jobs.length === 0) return 'all';
    if (hasParsedWeekScope) {
      return resolveWeekSelectionFromParsedScope(resolvedScope.week, jobs, rollingRangeAnchor);
    }
    return defaultToLast7Days ? last7DaysSelectionFromJobs(jobs, rollingRangeAnchor) : 'all';
  }, [
    defaultToLast7Days,
    hasParsedWeekScope,
    jobs,
    jobsTrustworthy,
    mode,
    resolvedScope.week,
    rollingRangeAnchor,
  ]);
  const effectiveWeekSelection = useMemo((): WeekSelectorValue => {
    const selection = resolvedRouteWeekSelection ?? weekSelection;
    if (
      mode === 'with-week' &&
      selection !== 'all' &&
      isRollingWeekSelection(selection) &&
      jobsTrustworthy &&
      jobs.length > 0
    ) {
      return rangeSelectionFromJobs(jobs, selection.rangeKey, rollingRangeAnchor);
    }
    return selection;
  }, [jobs, jobsTrustworthy, mode, resolvedRouteWeekSelection, rollingRangeAnchor, weekSelection]);

  const replaceScopeInUrl = useCallback(
    (next: {
      weekSelection?: WeekSelectorValue;
      periodGranularity?: PeriodGranularity;
      engine?: string;
      promptGroupId?: string;
      promptSubtopicId?: string;
      promptIds?: string[];
      promptId?: string;
      topicFilter?: PromptTopicFilter | null;
    }) => {
      const rawFilterToPreserve =
        next.topicFilter === undefined &&
        next.promptGroupId === undefined &&
        next.promptSubtopicId === undefined
          ? searchParams.get('topic_filter')
          : null;
      const nextFilter =
        next.topicFilter === undefined
          ? next.promptGroupId !== undefined || next.promptSubtopicId !== undefined
            ? undefined
            : resolvedScope.topicFilter
          : next.topicFilter || undefined;
      const nextChild =
        next.promptSubtopicId ??
        (next.promptGroupId === undefined ? resolvedScope.promptSubtopicId : undefined);
      const nextPromptIds =
        next.promptIds ??
        (next.promptGroupId !== undefined || next.promptSubtopicId !== undefined
          ? []
          : scopedPromptIds);
      const preservePrompt = (params: URLSearchParams) => {
        const promptId =
          next.promptId ??
          (next.promptGroupId !== undefined || next.promptSubtopicId !== undefined
            ? ''
            : selectedPromptId);
        if (promptId) params.set('prompt_id', promptId);
        else params.delete('prompt_id');
        if (
          next.engine !== undefined ||
          next.promptGroupId !== undefined ||
          next.promptSubtopicId !== undefined ||
          next.promptId !== undefined ||
          next.topicFilter !== undefined ||
          next.promptIds !== undefined
        ) {
          for (const key of [
            'page',
            'cursor_created_at',
            'cursor_id',
            'cursor_citation_count',
            'cursor_url',
          ])
            params.delete(key);
        }
      };
      if (
        mode === 'with-week' &&
        (jobsTrustworthy ||
          next.weekSelection !== undefined ||
          next.periodGranularity !== undefined)
      ) {
        const nextWeekSelection = next.weekSelection ?? effectiveWeekSelection;
        const nextGranularity = next.periodGranularity ?? periodGranularity;
        const normalizedSelection = normalizeWeekSelectionForGranularity(
          nextWeekSelection,
          nextGranularity
        );
        const periodScope = periodSelectionFromWeekSelection(normalizedSelection);

        const scopeParams = buildBrandScopeSearchParams({
          weekSelection: normalizedSelection,
          periodGranularity: periodScope?.period_grain ?? nextGranularity,
          engine: typeof next.engine === 'string' ? next.engine : scopedEngine,
          promptGroupId:
            typeof next.promptGroupId === 'string'
              ? next.promptGroupId
              : resolvedScope.promptGroupId,
          promptSubtopicId: nextChild,
          promptGroupOrigin:
            next.promptGroupId === undefined
              ? resolvedScope.promptGroupOrigin
              : topicOrigin(promptGroups.find(group => group.id === next.promptGroupId)),
          promptIds: nextPromptIds,
          topicFilter: nextFilter,
        });
        const params = new URLSearchParams(searchParams.toString());
        for (const key of [
          'range',
          'start_date',
          'end_date',
          'period_grain',
          'period_key',
          'week',
          'job_id',
          'job_ids',
          'engine',
          'prompt_group',
          'prompt_group_id',
          'prompt_group_origin',
          'prompt_subtopic_id',
          'prompt_ids',
          'topic_filter',
        ])
          params.delete(key);
        for (const [key, value] of scopeParams) params.set(key, value);
        if (!nextFilter && rawFilterToPreserve) params.set('topic_filter', rawFilterToPreserve);
        preservePrompt(params);
        const search = params.toString();
        window.history.replaceState(null, '', `${pathname}${search ? `?${search}` : ''}`);
        return;
      }

      const params = new URLSearchParams(searchParams.toString());
      const normalizedEngine = (typeof next.engine === 'string' ? next.engine : scopedEngine || '')
        .trim()
        .toLowerCase();
      if (normalizedEngine) {
        params.set('engine', normalizedEngine);
      } else {
        params.delete('engine');
      }

      params.delete('prompt_group');
      params.delete('prompt_group_id');
      params.delete('prompt_group_origin');
      params.delete('prompt_subtopic_id');
      params.delete('prompt_ids');
      params.delete('topic_filter');
      if (nextFilter) params.set('topic_filter', JSON.stringify(nextFilter));
      else if (rawFilterToPreserve) params.set('topic_filter', rawFilterToPreserve);
      const normalizedPromptGroupId =
        typeof next.promptGroupId === 'string'
          ? next.promptGroupId.trim()
          : resolvedScope.promptGroupId;
      if (normalizedPromptGroupId) {
        params.set('prompt_group', normalizedPromptGroupId);

        if (
          next.promptGroupId === undefined
            ? resolvedScope.promptGroupOrigin === 'shared'
            : topicOrigin(promptGroups.find(group => group.id === normalizedPromptGroupId))
        )
          params.set('prompt_group_origin', 'shared');
      }
      if (nextChild) params.set('prompt_subtopic_id', nextChild);
      const normalizedPromptIds = Array.from(
        new Set(nextPromptIds.map(value => String(value || '').trim()).filter(Boolean))
      ).sort();
      if (!nextFilter && !rawFilterToPreserve && normalizedPromptIds.length > 0) {
        params.set('prompt_ids', normalizedPromptIds.join(','));
      }

      preservePrompt(params);
      const search = params.toString();
      window.history.replaceState(null, '', `${pathname}${search ? `?${search}` : ''}`);
    },
    [
      mode,
      jobsTrustworthy,
      pathname,
      periodGranularity,
      resolvedScope.promptGroupId,
      resolvedScope.promptGroupOrigin,
      resolvedScope.promptSubtopicId,
      resolvedScope.topicFilter,
      promptGroups,
      scopedEngine,
      scopedPromptIds,
      searchParams,
      selectedPromptId,
      effectiveWeekSelection,
    ]
  );

  useEffect(() => {
    if (
      !topicsHydrated ||
      resolvedScope.topicFilter ||
      !resolvedScope.promptGroupId ||
      resolvedScope.promptSubtopicId
    )
      return;
    if (
      !promptGroups.some(
        group =>
          group.id === resolvedScope.promptGroupId &&
          topicOrigin(group) === resolvedScope.promptGroupOrigin
      )
    ) {
      replaceScopeInUrl({ promptGroupId: '', promptSubtopicId: '', promptIds: [], promptId: '' });
    }
  }, [
    topicsHydrated,
    promptGroups,
    resolvedScope.promptGroupId,
    resolvedScope.promptGroupOrigin,
    resolvedScope.promptSubtopicId,
    replaceScopeInUrl,
  ]);

  useEffect(() => {
    if (topicsHydrated && childScopeValid && !promptBelongsToScope)
      replaceScopeInUrl({ promptId: '' });
  }, [topicsHydrated, childScopeValid, promptBelongsToScope, replaceScopeInUrl]);

  const handlePromptChange = useCallback(
    (promptId: string) => {
      if (!promptId) return replaceScopeInUrl({ promptId });
      const outsideParent =
        (resolvedScope.promptGroupId && !selectedTopic?.prompt_ids.includes(promptId)) ||
        (resolvedScope.promptSubtopicId && !selectedSubtopic?.prompt_ids.includes(promptId));
      replaceScopeInUrl({
        promptId,
        promptIds: [],
        topicFilter: null,
        ...(outsideParent ? { promptGroupId: '', promptSubtopicId: '' } : {}),
      });
    },
    [
      replaceScopeInUrl,
      resolvedScope.promptGroupId,
      resolvedScope.promptSubtopicId,
      selectedTopic,
      selectedSubtopic,
    ]
  );
  const handlePromptScopeChange = useCallback(
    (
      promptGroupId: string,
      promptId: string,
      promptSubtopicId = '',
      filter?: AppliedPromptTopicFilter
    ) => {
      replaceScopeInUrl({
        promptGroupId,
        promptSubtopicId,
        promptId,
        topicFilter: filter ? { include: filter.include, exclude: filter.exclude } : null,
        promptIds: [],
      });
    },
    [replaceScopeInUrl]
  );

  const handlePromptSubtopicChange = useCallback(
    (promptSubtopicId: string) => {
      replaceScopeInUrl({ promptSubtopicId, promptId: '', promptIds: [] });
    },
    [replaceScopeInUrl]
  );

  const handleWeekChange = useCallback(
    (next: WeekSelectorValue) => {
      if (mode !== 'with-week') return;
      setWeekSelection(next);
      replaceScopeInUrl({ weekSelection: next });
    },
    [mode, replaceScopeInUrl]
  );

  const handlePeriodGranularityChange = useCallback(
    (next: PeriodGranularity) => {
      if (mode !== 'with-week') return;
      setPeriodGranularity(next);
      const normalizedSelection = normalizeWeekSelectionForGranularity(
        effectiveWeekSelection,
        next
      );
      setWeekSelection(normalizedSelection);
      replaceScopeInUrl({
        periodGranularity: next,
        weekSelection: normalizedSelection,
      });
    },
    [effectiveWeekSelection, mode, replaceScopeInUrl]
  );

  const handleEngineChange = useCallback(
    (nextEngine: string) => {
      replaceScopeInUrl({ engine: nextEngine });
    },
    [replaceScopeInUrl]
  );

  const handlePromptGroupChange = useCallback(
    (nextPromptGroupId: string) => {
      if (!nextPromptGroupId) {
        replaceScopeInUrl({ promptGroupId: '', promptSubtopicId: '', promptIds: [], promptId: '' });
        return;
      }
      replaceScopeInUrl({
        promptGroupId: nextPromptGroupId,
        promptSubtopicId: '',
        promptIds: [],
        promptId: '',
      });
    },
    [promptGroups, replaceScopeInUrl]
  );

  const handleClearFilters = useCallback(() => {
    if (mode === 'with-week') {
      const defaultSelection =
        defaultToLast7Days && jobsTrustworthy
          ? last7DaysSelectionFromJobs(jobs, rollingRangeAnchor)
          : 'all';
      setWeekSelection(defaultSelection);
      replaceScopeInUrl({
        weekSelection: defaultSelection,
        engine: '',
        promptGroupId: '',
        promptIds: [],
        promptId: '',
      });
      return;
    }
    replaceScopeInUrl({
      engine: '',
      promptGroupId: '',
      promptIds: [],
      promptId: '',
    });
  }, [
    defaultToLast7Days,
    jobs,
    jobsTrustworthy,
    mode,
    periodGranularity,
    replaceScopeInUrl,
    rollingRangeAnchor,
  ]);

  const handleClearCustomPromptScope = useCallback(() => {
    replaceScopeInUrl({ promptGroupId: '', promptSubtopicId: '', promptIds: [], promptId: '' });
  }, [replaceScopeInUrl]);

  if (mode === 'with-week') {
    return {
      selectedPromptId,
      topicScopeReady,
      handlePromptChange,
      handlePromptScopeChange,
      handlePromptSubtopicChange,
      resolvedScope,
      sqlPromptGroupId: resolvedScope.promptGroupId || undefined,
      promptGroupOrigin: resolvedScope.promptGroupOrigin,
      promptSubtopicId: resolvedScope.promptSubtopicId,
      scopedPromptIds,
      scopedEngine,
      hasCustomPromptScope,
      periodGranularity,
      setPeriodGranularity,
      handlePeriodGranularityChange,
      weekSelection: effectiveWeekSelection,
      setWeekSelection,
      replaceScopeInUrl,
      handleWeekChange,
      handleEngineChange,
      handlePromptGroupChange,
      handleClearFilters,
      handleClearCustomPromptScope,
    };
  }

  return {
    selectedPromptId,
    topicScopeReady,
    handlePromptChange,
    handlePromptScopeChange,
    handlePromptSubtopicChange,
    resolvedScope,
    sqlPromptGroupId: resolvedScope.promptGroupId || undefined,
    promptGroupOrigin: resolvedScope.promptGroupOrigin,
    promptSubtopicId: resolvedScope.promptSubtopicId,
    scopedPromptIds,
    scopedEngine,
    hasCustomPromptScope,
    periodGranularity: null,
    setPeriodGranularity: null,
    handlePeriodGranularityChange: null,
    weekSelection: null,
    setWeekSelection: null,
    replaceScopeInUrl,
    handleWeekChange: null,
    handleEngineChange,
    handlePromptGroupChange,
    handleClearFilters,
    handleClearCustomPromptScope,
  };
}
